/**
 * MPlusSystem — OWNER: mplus builder (packages/sim/src/mplus/**).
 * Keystone lifecycle (pre → running → completed/depleted), timer, forces, scaling + affixes,
 * death penalty, wipes and run-back, chest, and pull/encounter segment bookkeeping.
 *
 * Phases: 'completed' = finished inside the timer, 'depleted' = finished over time.
 * The run keeps going after the timer runs out; the phase changes only when it ends.
 */
import { worldLootResult } from '../worldloot/index'; // +world-resources
import type {
  AffixId,
  MPlusBook as ContractMPlusBook,
  EncounterId,
  KeystoneConfig,
  MPlusState,
  MPlusSystem,
  Ms,
  PackId,
  RunResult,
  SimContext,
  SimEventType,
  Unit,
  UnitId,
  Vec2,
  World,
} from '../contracts';
import { TICK_MS } from '../contracts';
import { levelScaling } from '../field/level'; // +field
import { DEATH_PENALTY_MS, RELEASE_DELAY_MS, START_GATE_RADIUS, WIPE_RESPAWN_DELAY_MS, chestFor, npcScaling } from './scaling';

export * from './scaling';

/** Bookkeeping lives on world.mplus.book (contracts.MPlusBook) so it survives snapshot/restore. */
export type MPlusBook = ContractMPlusBook;
export type SegmentBook = NonNullable<MPlusBook['segment']>;
export type EncounterBook = NonNullable<MPlusBook['encounter']>;

export function mplusBook(world: World): MPlusBook {
  const m = world.mplus;
  m.book ??= { segment: null, encounter: null, respawnAt: null };
  return m.book;
}

/** Party respawn offsets around the entrance (same layout as the initial spawn). */
const RESPAWN_FORMATION: Vec2[] = [
  { x: 2, y: 0 },
  { x: -2, y: 0 },
  { x: 0, y: 2 },
  { x: 0, y: -2 },
];

function freshState(keystone: KeystoneConfig, timerMs: Ms, forcesRequired: number): MPlusState {
  return {
    phase: 'pre',
    level: keystone.level,
    affixes: [...keystone.affixes],
    timerMs,
    startedAt: null,
    elapsedMs: 0,
    forces: 0,
    forcesRequired,
    deaths: 0,
    deathPenaltyMs: 0,
    bossesKilled: [],
    packsKilled: [],
    completedAt: null,
    chest: null,
    inCombat: false,
    combatSince: null,
    outOfCombatMs: 0,
    pulls: 0,
    wipes: 0,
    book: { segment: null, encounter: null, respawnAt: null },
  };
}

const isActive = (m: MPlusState) => m.phase === 'running';
/** +field: keystone level 0 = normal difficulty (no timer, no affixes, no keystone scaling). */
const isNormal = (m: MPlusState) => m.level <= 0;
/** +field: open fields and towns have no run at all (no timer, segments, wipes or completion). */
const isFieldMap = (ctx: SimContext) => !!ctx.content.dungeon(ctx.world.dungeonId)?.field;

/** Alive enemies that are fighting (combat flag set by CombatSystem, or AI combat mode). */
function engagedEnemies(world: World): Unit[] {
  const out: Unit[] = [];
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (u && u.alive && u.faction === 'enemy' && (u.inCombat || u.ai?.mode === 'combat')) out.push(u);
  }
  return out;
}

/** Pack a unit belongs to (summons inherit their summoner's pack). */
function packOf(world: World, u: Unit): PackId | null {
  if (u.packId) return u.packId;
  const summoner = u.summonerId ? world.units[u.summonerId] : undefined;
  return summoner?.packId ?? null;
}

function packIdsOf(world: World, units: readonly Unit[]): PackId[] {
  const ids: PackId[] = [];
  for (const u of units) {
    const p = packOf(world, u);
    if (p && !ids.includes(p)) ids.push(p);
  }
  return ids;
}

function packMembers(world: World, packId: PackId): Unit[] {
  const listed = world.packs[packId]?.memberIds;
  const ids = listed ?? world.unitOrder.filter((id) => world.units[id]?.packId === packId);
  return ids.map((id) => world.units[id]).filter((u): u is Unit => !!u);
}

function encounterIdOfPack(ctx: SimContext, packId: PackId | null): EncounterId | null {
  if (!packId) return null;
  const def = ctx.content.dungeon(ctx.world.dungeonId)?.packs.find((p) => p.id === packId);
  return def?.encounterId ?? null;
}

/** True if an event of this type for this encounter was already emitted this tick (e.g. by AI). */
function emittedThisTick(world: World, type: SimEventType, encounterId: EncounterId): boolean {
  return world.events.some((e) => e.type === type && 'encounterId' in e && e.encounterId === encounterId);
}

export function createMPlusSystem(): MPlusSystem {
  const startSegment = (ctx: SimContext, packIds: PackId[]): SegmentBook => {
    const m = ctx.world.mplus;
    m.pulls += 1;
    m.inCombat = true;
    m.combatSince = ctx.now;
    const seg: SegmentBook = { pull: m.pulls, startMs: ctx.now, packIds: [...packIds], killedIds: [] };
    mplusBook(ctx.world).segment = seg;
    ctx.emit({ type: 'pull_start', pull: seg.pull, packIds: [...seg.packIds] });
    return seg;
  };

  const endSegment = (ctx: SimContext, wipe: boolean): void => {
    const b = mplusBook(ctx.world);
    const seg = b.segment;
    if (!seg) return;
    ctx.emit({ type: 'pull_end', pull: seg.pull, packIds: [...seg.packIds], killedIds: [...seg.killedIds], wipe });
    b.segment = null;
    ctx.world.mplus.inCombat = false;
    ctx.world.mplus.combatSince = null;
  };

  const encounterName = (ctx: SimContext, id: EncounterId) => ctx.content.encounter(id)?.name ?? id;

  const endEncounter = (ctx: SimContext, kill: boolean): void => {
    const b = mplusBook(ctx.world);
    const enc = b.encounter;
    if (!enc) return;
    if (!emittedThisTick(ctx.world, 'encounter_end', enc.encounterId)) {
      ctx.emit({ type: 'encounter_end', encounterId: enc.encounterId, name: encounterName(ctx, enc.encounterId), kill });
    }
    b.encounter = null;
  };

  /** The encounter currently in progress: AI's world.encounter, else an engaged boss pack. */
  const activeEncounter = (ctx: SimContext, engaged: readonly Unit[]): EncounterBook | null => {
    const w = ctx.world;
    if (w.encounter && w.encounter.result === null) {
      return { encounterId: w.encounter.encounterId, packId: w.encounter.packId, startedAt: w.encounter.startedAt };
    }
    for (const u of engaged) {
      const packId = packOf(w, u);
      const encId = u.encounterId ?? encounterIdOfPack(ctx, packId);
      if (encId && !w.mplus.bossesKilled.includes(encId)) return { encounterId: encId, packId, startedAt: ctx.now };
    }
    return null;
  };

  const updateEncounter = (ctx: SimContext, engaged: readonly Unit[]): void => {
    const b = mplusBook(ctx.world);
    const active = activeEncounter(ctx, engaged);
    if (b.encounter) {
      const id = b.encounter.encounterId;
      const killed = ctx.world.mplus.bossesKilled.includes(id) || (ctx.world.encounter?.encounterId === id && ctx.world.encounter.result === 'kill');
      if (killed || active?.encounterId !== id) endEncounter(ctx, killed);
    }
    if (!b.encounter && active && !ctx.world.mplus.bossesKilled.includes(active.encounterId)) {
      b.encounter = active;
      if (!emittedThisTick(ctx.world, 'encounter_start', active.encounterId)) {
        ctx.emit({ type: 'encounter_start', encounterId: active.encounterId, name: encounterName(ctx, active.encounterId) });
      }
    }
  };

  /** Graveyard: the last killed boss's spawn (walkable, cleared), else the entrance. */
  const checkpoint = (ctx: SimContext): Vec2 => {
    const w = ctx.world;
    const dungeon = ctx.content.dungeon(w.dungeonId);
    const killed = w.mplus.bossesKilled;
    for (let i = killed.length - 1; i >= 0; i--) {
      const pack = dungeon?.packs.find((p) => p.encounterId === killed[i]);
      const at = pack?.spawns[0]?.pos;
      if (at) return { x: at.x, y: at.y };
    }
    return dungeon?.entrance ?? { x: 0, y: 0 };
  };

  const releaseOne = (ctx: SimContext, id: UnitId, at: Vec2, slot: number): void => {
    const u = ctx.world.units[id];
    if (!u || u.alive || u.departed) return;
    const off = RESPAWN_FORMATION[slot % RESPAWN_FORMATION.length]!;
    const pos = ctx.sys.spatial.clampToWalkable(ctx.world, { x: at.x + off.x, y: at.y + off.y });
    ctx.sys.combat.revive(ctx, id, 1, pos);
    u.pos = pos;
    u.hp = u.maxHp;
    u.move = null;
    u.cast = null;
    u.queued = null;
    u.targetId = null;
    u.inCombat = false;
    const cls = u.classId ? ctx.content.class(u.classId) : undefined;
    for (const r of cls?.resources ?? []) {
      const pool = u.resources[r.id];
      if (pool) pool.cur = r.start;
    }
  };

  const respawnParty = (ctx: SimContext): void => {
    const w = ctx.world;
    const dungeon = ctx.content.dungeon(w.dungeonId);
    const entrance = dungeon?.entrance ?? { x: 0, y: 0 };
    w.partyIds.forEach((id, i) => {
      const u = w.units[id];
      if (!u || u.departed) return; // +personas: members who left stay gone
      const off = RESPAWN_FORMATION[i % RESPAWN_FORMATION.length]!;
      const pos = { x: entrance.x + off.x, y: entrance.y + off.y };
      if (!u.alive) ctx.sys.combat.revive(ctx, id, 1, pos);
      u.pos = pos;
      u.hp = u.maxHp;
      u.facing = dungeon?.entranceFacing ?? u.facing;
      u.move = null;
      u.cast = null;
      u.queued = null;
      u.targetId = null;
      u.inCombat = false;
      // Resources go back to their spawn values; cooldowns are kept.
      const cls = u.classId ? ctx.content.class(u.classId) : undefined;
      for (const r of cls?.resources ?? []) {
        const pool = u.resources[r.id];
        if (pool) pool.cur = r.start;
      }
    });
    mplusBook(w).respawnAt = null;
  };

  const checkCompletion = (ctx: SimContext): void => {
    const m = ctx.world.mplus;
    const required = ctx.content.dungeon(ctx.world.dungeonId)?.requiredEncounters ?? [];
    // +field: normal difficulty ignores forces (unless the dungeon has no required encounters).
    if (m.forces < m.forcesRequired && !(isNormal(m) && required.length > 0)) return;
    if (!required.every((id) => m.bossesKilled.includes(id))) return;
    endSegment(ctx, false);
    endEncounter(ctx, true);
    m.elapsedMs = ctx.now - (m.startedAt ?? ctx.now) + m.deathPenaltyMs;
    m.chest = isNormal(m) ? 1 : chestFor(m.elapsedMs, m.timerMs); // +field: normal has no timer
    m.phase = m.chest > 0 ? 'completed' : 'depleted';
    m.completedAt = ctx.now;
    ctx.emit({ type: 'run_end', result: sys.result(ctx.world) });
  };

  const abandon = (ctx: SimContext): void => {
    const m = ctx.world.mplus;
    endSegment(ctx, false);
    endEncounter(ctx, false);
    m.abandoned = true;
    m.elapsedMs = ctx.now - (m.startedAt ?? ctx.now) + m.deathPenaltyMs;
    m.chest = 0;
    m.phase = 'depleted';
    m.completedAt = ctx.now;
    ctx.emit({ type: 'run_end', result: sys.result(ctx.world) });
  };

  const groupDisbanded = (ctx: SimContext): boolean => {
    const w = ctx.world;
    const m = w.mplus;
    const left = w.partyIds.filter((id) => w.units[id]?.departed);
    if (left.length === 0) return false;
    const stay = w.partyIds.map((id) => w.units[id]).filter((u): u is Unit => !!u && !u.departed);
    const overTime = m.timerMs > 0 && ctx.now - (m.startedAt ?? ctx.now) + m.deathPenaltyMs > m.timerMs; // +field: normal has no timer
    return stay.length <= 1 || !stay.some((u) => u.role === 'tank') || overTime;
  };

  const partyWiped = (w: World): boolean => w.partyIds.length > 0 && w.partyIds.every((id) => !w.units[id]?.alive);

  const gateCrossed = (ctx: SimContext): boolean => {
    const entrance = ctx.content.dungeon(ctx.world.dungeonId)?.entrance;
    if (!entrance) return false;
    return ctx.world.partyIds.some((id) => {
      const u = ctx.world.units[id];
      return !!u && u.alive && Math.hypot(u.pos.x - entrance.x, u.pos.y - entrance.y) > START_GATE_RADIUS;
    });
  };

  const sys: MPlusSystem = {
    init(ctx, keystone) {
      const dungeon = ctx.content.dungeon(ctx.world.dungeonId);
      // +field: normal difficulty (level 0) and fields have no timer and no affixes.
      const untimed = keystone.level <= 0 || !!dungeon?.field;
      ctx.world.mplus = freshState(untimed ? { level: keystone.level, affixes: [] } : keystone, untimed ? 0 : (dungeon?.timerMs ?? 0), dungeon?.forcesRequired ?? 0);
    },

    scaling(ctx, npcId) {
      // +field: field mobs are scaled by their own level (field system); levelled dungeons
      // (DungeonDef.mobLevel) multiply levelScaling into the keystone scaling; level 0 = ×1.
      const dungeon = ctx.content.dungeon(ctx.world.dungeonId);
      if (dungeon?.field) return { hp: 1, damage: 1 };
      const m = ctx.world.mplus;
      const tier = ctx.content.npc(npcId)?.tier ?? 'standard';
      const key = isNormal(m) ? { hp: 1, damage: 1 } : npcScaling(m.level, m.affixes, tier);
      if (dungeon?.mobLevel === undefined) return key;
      const lv = levelScaling(dungeon.mobLevel);
      return { hp: key.hp * lv.hp, damage: key.damage * lv.damage };
    },

    startRun(ctx) {
      const m = ctx.world.mplus;
      if (m.phase !== 'pre' || isFieldMap(ctx)) return;
      m.phase = 'running';
      m.startedAt = ctx.now;
      m.elapsedMs = 0;
      ctx.emit({ type: 'run_start', dungeonId: ctx.world.dungeonId, level: m.level, affixes: [...m.affixes], timerMs: m.timerMs });
    },

    update(ctx) {
      const w = ctx.world;
      const m = w.mplus;
      if (isFieldMap(ctx)) return; // +field
      const b = mplusBook(w);
      let engaged = engagedEnemies(w);
      if (m.phase === 'pre') {
        if (engaged.length === 0 && !gateCrossed(ctx)) return;
        sys.startRun(ctx);
      }
      if (!isActive(m)) return;
      // +personas: the group falls apart once members left: everyone gone, one left, no tank left
      // (nobody pulls), or the key already over time. The key is abandoned (ends depleted, no chest).
      if (groupDisbanded(ctx)) return abandon(ctx);

      if (b.respawnAt !== null && ctx.now >= b.respawnAt) respawnParty(ctx);
      if (b.respawnAt === null && partyWiped(w)) {
        sys.onWipe(ctx);
        engaged = engagedEnemies(w);
      }

      // Combat segments: one pull spans from the first engaged enemy to the last one leaving combat.
      if (!b.segment && engaged.length > 0 && b.respawnAt === null) startSegment(ctx, packIdsOf(w, engaged));
      else if (b.segment) {
        for (const p of packIdsOf(w, engaged)) if (!b.segment.packIds.includes(p)) b.segment.packIds.push(p);
        if (engaged.length === 0) endSegment(ctx, false);
      }

      // Release: once the party is out of combat, dead members respawn at the checkpoint.
      if (b.respawnAt === null && !b.segment && w.partyIds.some((id) => w.units[id] && !w.units[id]!.alive && !w.units[id]!.departed)) {
        b.releaseAt ??= ctx.now + RELEASE_DELAY_MS;
        if (ctx.now >= b.releaseAt) {
          const at = checkpoint(ctx);
          w.partyIds.forEach((id, i) => releaseOne(ctx, id, at, i));
          b.releaseAt = null;
        }
      } else if (b.segment || b.respawnAt !== null) b.releaseAt = null;

      updateEncounter(ctx, engaged);
      if (!b.segment) m.outOfCombatMs += TICK_MS;
      m.elapsedMs = ctx.now - (m.startedAt ?? ctx.now) + m.deathPenaltyMs;
      checkCompletion(ctx);
    },

    onDeath(ctx, unitId) {
      const w = ctx.world;
      const m = w.mplus;
      const u = w.units[unitId];
      if (!u || m.phase === 'completed' || m.phase === 'depleted' || isFieldMap(ctx)) return;

      if (u.kind === 'player') {
        if (!isActive(m)) return;
        m.deaths += 1;
        if (!isNormal(m)) m.deathPenaltyMs += DEATH_PENALTY_MS; // +field: no timer, no penalty
        m.elapsedMs = ctx.now - (m.startedAt ?? ctx.now) + m.deathPenaltyMs;
        return;
      }
      if (u.faction !== 'enemy') return;

      const b = mplusBook(w);
      const packId = packOf(w, u);
      if (!b.segment && isActive(m)) startSegment(ctx, packId ? [packId] : []);
      if (b.segment && !b.segment.killedIds.includes(unitId)) b.segment.killedIds.push(unitId);

      const forces = u.npcId ? (ctx.content.npc(u.npcId)?.forces ?? 0) : 0;
      if (forces > 0) {
        const next = m.forcesRequired > 0 ? Math.min(m.forcesRequired, m.forces + forces) : m.forces + forces;
        if (next !== m.forces) {
          m.forces = next;
          ctx.emit({ type: 'forces_update', forces: m.forces, forcesRequired: m.forcesRequired, targetID: unitId });
        }
      }

      if (!packId) return;
      const members = packMembers(w, packId);
      if (!m.packsKilled.includes(packId) && members.every((x) => !x.alive)) {
        m.packsKilled.push(packId);
        const ps = w.packs[packId];
        if (ps) ps.state = 'dead';
      }
      const encId = u.encounterId ?? encounterIdOfPack(ctx, packId);
      const enc = encId ? ctx.content.encounter(encId) : undefined;
      if (encId && !m.bossesKilled.includes(encId)) {
        const bosses = members.filter((x) => (enc ? x.npcId !== null && enc.bossNpcIds.includes(x.npcId) : x.tier === 'boss'));
        if (bosses.length > 0 && bosses.every((x) => !x.alive)) {
          m.bossesKilled.push(encId);
          if (!w.encountersDone.includes(encId)) w.encountersDone.push(encId);
        }
      }
    },

    releaseSpirit(ctx, unitId) {
      const w = ctx.world;
      const slot = w.partyIds.indexOf(unitId);
      if (slot < 0 || mplusBook(w).respawnAt !== null || isFieldMap(ctx)) return;
      releaseOne(ctx, unitId, checkpoint(ctx), slot);
    },

    onWipe(ctx) {
      const w = ctx.world;
      const b = mplusBook(w);
      if (b.respawnAt !== null || isFieldMap(ctx)) return;
      w.mplus.wipes += 1;
      endSegment(ctx, true);
      endEncounter(ctx, false);
      if (w.encounter && w.encounter.result === null) w.encounter.result = 'wipe';
      // Engaged enemies (bosses included) reset; killed trash stays dead.
      for (const u of engagedEnemies(w)) ctx.sys.ai.evade(ctx, u.id);
      b.respawnAt = ctx.now + WIPE_RESPAWN_DELAY_MS;
    },

    result(world: World): RunResult {
      const m = world.mplus;
      const completed = (m.phase === 'completed' || m.phase === 'depleted') && !m.abandoned;
      const chest = m.chest ?? 0;
      const departed = world.partyIds.filter((id) => world.units[id]?.departed).length; // +personas
      return {
        dungeonId: world.dungeonId,
        level: m.level,
        affixes: [...m.affixes],
        completed,
        timed: completed && chest > 0,
        chest,
        elapsedMs: m.elapsedMs,
        timerMs: m.timerMs,
        deaths: m.deaths,
        deathPenaltyMs: m.deathPenaltyMs,
        forces: m.forces,
        forcesRequired: m.forcesRequired,
        outOfCombatMs: m.outOfCombatMs,
        pulls: m.pulls,
        wipes: m.wipes,
        bossesKilled: m.bossesKilled.length, // +world-resources
        ...worldLootResult(world), // +world-resources
        ...(m.abandoned ? { abandoned: true } : {}), // +personas
        ...(departed > 0 ? { departed } : {}), // +personas
      };
    },
  };
  return sys;
}

/**
 * Default affixes by key level (PLAN §6, Fortified/Tyrannical alternate by level band):
 * below +4 none; +4..+9 one of them (alternating by `week`, even = Fortified); +10 and up
 * both. Both front ends (web, harness) use this unless the user picks affixes explicitly.
 */
export function affixesForLevel(level: number, week = 0): AffixId[] {
  if (level < 4) return [];
  const first: AffixId = week % 2 === 0 ? 'fortified' : 'tyrannical';
  if (level < 10) return [first];
  return [first, first === 'fortified' ? 'tyrannical' : 'fortified'];
}
